import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Cart gift offer component.
 * Customer must tick a checkbox and (optionally) select a variant to add the free product to cart.
 *
 * @typedef {Object} CartGiftOfferRefs
 * @property {HTMLSelectElement | HTMLInputElement} variantSelect
 * @property {HTMLButtonElement} addButton
 */

/**
 * @extends {Component<CartGiftOfferRefs>}
 */
class CartGiftOfferComponent extends Component {
  requiredRefs = ['variantSelect', 'addButton'];

  connectedCallback() {
    super.connectedCallback();
    if (this.refs.variantSelect instanceof HTMLSelectElement) {
      this.refs.variantSelect.addEventListener('change', this.#updateButtonState);
    }
    this.refs.addButton.addEventListener('click', this.#addToCart);
  }

  #updateButtonState = () => {
    this.refs.addButton.disabled = !this.#getVariantId();
  };

  #getVariantId = () => {
    const el = this.refs.variantSelect;
    const value = 'value' in el ? el.value : '';
    return value && value !== '' ? value : null;
  };

  #addToCart = async () => {
    const variantId = this.#getVariantId();
    if (!variantId || this.refs.addButton.disabled) return;

    const button = this.refs.addButton;
    button.disabled = true;
    button.classList.add('cart-gift-offer__add-button--loading');

    try {
      const sectionId = this.dataset.sectionId;
      const sections = sectionId ? [sectionId] : [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id && !sections.includes(id)) sections.push(id);
      });

      const config = fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        console.error('Error adding gift to cart:', data.description);
        button.disabled = false;
        button.classList.remove('cart-gift-offer__add-button--loading');
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, variantId, {
          source: 'cart-gift-offer-component',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      if (data.sections) {
        for (const [id, html] of Object.entries(data.sections)) {
          morphSection(id, /** @type {string} */ (html));
        }
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
      button.disabled = false;
      button.classList.remove('cart-gift-offer__add-button--loading');
    }
  };
}

if (!customElements.get('cart-gift-offer-component')) {
  customElements.define('cart-gift-offer-component', CartGiftOfferComponent);
}
